import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

import App from './App.vue';
import router from './router';
import i18n from './i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import API from './classes/API';
import { reloadOnceForChunkError } from './utility/chunkReload';
import { bootTrace } from './utility/bootTrace';

import * as Sentry from "@sentry/vue";
import { filterBreadcrumb, filterTransactionSpans, passesSessionLimit, scrubEvent } from './utility/sentryFilters';

// si è scelto di importare bootstrap in main ts per poterlo gestire correttamente nello scope degli stili dei componenti
// altrimenti andrebbero in sovrapposizione con quelli applicati da WordPress
// import "bootstrap-italia/dist/css/bootstrap-italia.min.css";
// import "bootstrap-italia/dist/js/bootstrap-italia.bundle.min.js";

// plugins
// import installModals from "@/plugins/modals";

// Vite preload error: after a new deploy, old hashed chunks/CSS no longer exist.
// Force a page reload so the browser fetches the new assets — ma una sola volta: se
// l'asset è irraggiungibile per altri motivi, un reload incondizionato manda la pagina
// in loop. Se il reload è già stato tentato lasciamo propagare l'errore, così il router
// lo intercetta e App.vue mostra un messaggio azionabile.
window.addEventListener('vite:preloadError', (event) => {
	if (reloadOnceForChunkError()) {
		event.preventDefault();
	}
});

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);
app.use(i18n);

// `app.use(router)` avvia da solo la navigazione iniziale: da qui in poi siamo dentro
// il percorso che, se non si chiude, lascia la pagina sul loader.
bootTrace('main: app.use(router) — avvio navigazione iniziale');
app.use(router);
router
	.isReady()
	.then(() => bootTrace('main: router PRONTO', router.currentRoute.value.fullPath))
	.catch((e) => bootTrace('main: router FALLITO', e));

// app.use(installModals);

// ignetto globalmente le dipendenze che mi serviranno, sicuramente
// all'interno dell'APP

// injection
const apiInstance = new API(import.meta.env.VITE_API_ENDPOINT);
app.provide('apiInstance', apiInstance);
//
app.component('FontAwesomeIcon', FontAwesomeIcon);

// Sentry
// se ho un environment specifico, inizializzo Sentry
if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
	Sentry.init({
		app,
		dsn: `https://229d4d3c5d98cd15a641c424473130f9@o4507368032370688.ingest.de.sentry.io/${import.meta.env.VITE_SENTRY_PROJECT_ID}`,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
		// Set via VITE_APP_VERSION in CI/CD (e.g. git tag). Used to correlate errors to deploys.
		release: import.meta.env.VITE_APP_VERSION,
		// L'app vive dentro una pagina WordPress: gli handler globali vedrebbero anche gli
		// script del tema, dei plugin e di terze parti. Accettiamo solo errori che nascono
		// dal nostro bundle.
		allowUrls: [/\/wp-content\/themes\/italiawp2-child\/inc\/VUE\/application\/dist\//],
		denyUrls: [
			/^chrome-extension:\/\//i,
			/^moz-extension:\/\//i,
			/^safari-(web-)?extension:\/\//i,
			/cookiebot/i,
			/googletagmanager\.com/i,
			/google\.com\/recaptcha/i,
			/gstatic\.com/i,
		],
		// Rumore del browser e condizioni attese, non bug dell'app
		ignoreErrors: [
			'ResizeObserver loop',
			'Non-Error promise rejection',
			'Script error.',
			'AbortError',
			'CanceledError',
			'Load failed',
			'NetworkError when attempting to fetch resource',
		],
		// Pochi breadcrumb bastano a ricostruire il contesto
		maxBreadcrumbs: 30,
		integrations: [
			// Automatically tracks Vue Router navigation as transactions and breadcrumbs.
			// Gli span sono la quota che si esaurisce per prima (5M/mese nel piano Team): long task,
			// long animation frame e INP aggiungono span senza servire a diagnosticare errori.
			Sentry.browserTracingIntegration({
				router,
				enableLongTask: false,
				enableLongAnimationFrame: false,
				enableInp: false,
			}),
			// Registra solo in caso di errore, con testo, input e media mascherati: l'app
			// tratta CF, IBAN, email, telefoni e OTP. Il replay ha una quota a parte e molto
			// più bassa degli errori: lo salviamo solo per gli errori veri, non per i warning
			// (es. watchdog di prima navigazione).
			Sentry.replayIntegration({
				maskAllText: true,
				maskAllInputs: true,
				blockAllMedia: true,
				beforeErrorSampling: (event) => event.level === 'error' || event.level === 'fatal',
			}),
		],
		// parseFloat is required — env vars arrive as strings
		tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0'),
		// Registra sessioni intere anche senza errori: spento di default. Il vecchio nome
		// VITE_SENTRY_REPLAYS_TRACES_SAMPLE_RATE (ingannevole) non è più letto.
		replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0'),
		replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '0.2'),
		beforeBreadcrumb: filterBreadcrumb,
		// Ogni caricamento di pagina porta con sé uno span per ogni css/js/img: sono la gran
		// parte degli span fatturati. Si tengono le richieste API e la navigazione.
		beforeSendTransaction: filterTransactionSpans,
		beforeSend(event) {
			if (!passesSessionLimit(event)) {
				return null;
			}
			return scrubEvent(event);
		},
	});
} else {
	// Con Sentry attivo l'errorHandler di Vue lo installa vueIntegration (nome e props del
	// componente in contexts.vue, console.error incluso): non va sovrascritto.
	// Senza Sentry l'errore di rendering resta comunque visibile in console
	app.config.errorHandler = (err, _vm, info) => {
		console.error('[Vue errorHandler]', info, err);
	};
}

bootTrace('main: app.mount()');
app.mount('#app');
bootTrace('main: app.mount() completato');